import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { axiosSingleton } from '../api/generalApi';
import { color } from '../styles/helpers/variables';
import { fontFamilyBody } from '../styles/helpers/mixins';
import { websiteStrings } from '../constants/websiteStrings';
import connect from '../store/connect';
import { fetchCart } from '../actions/actionCreators';
import { CART_API_BASE_URL } from '../constants/endpoints';

class CampaignCodeComponent extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isComponentExpanded: false,
      campaignCodeValue: undefined,
      invalidCampaignCode: undefined
    };

    this.formHandler = this.formHandler.bind(this);
    this.inputChangeHandler = this.inputChangeHandler.bind(this);
    this.removeCampaignCodeHandler = this.removeCampaignCodeHandler.bind(this);
    this.expandButtonHandler = this.expandButtonHandler.bind(this);
  }

  formHandler(event) {
    event.preventDefault();
    const { avardaSessionId } = this.props;
    const { campaignCodeValue } = this.state;

    if (campaignCodeValue) {
      axiosSingleton
        .post('/shoppingcart/setcampaigncode', { CampaignCode: campaignCodeValue, avardaSessionId })
        .then(response => {
          if (response.data.changeOccured) {
            this.props.fetchCart({ endpointUrl: `${CART_API_BASE_URL}/get`, method: 'GET' });
            if (window.AvardaCheckOutClient) {
              window.AvardaCheckOutClient.updateItems();
            }
          } else {
            this.setState({ invalidCampaignCode: campaignCodeValue });
          }
        })
        .catch(error => {
          console.error(error);
        });
    }
  }

  inputChangeHandler(event) {
    const campaignCodeValue = event.target.value;
    this.setState({ campaignCodeValue, invalidCampaignCode: undefined });
  }

  removeCampaignCodeHandler() {
    const { activeCampaignCode, avardaSessionId } = this.props;

    if (activeCampaignCode) {
      axiosSingleton
        .post('/shoppingcart/removecampaigncode', { CampaignCode: activeCampaignCode, avardaSessionId })
        .then(() => {
          this.props.fetchCart({ endpointUrl: `${CART_API_BASE_URL}/get`, method: 'GET' });
          if (window.AvardaCheckOutClient) {
            window.AvardaCheckOutClient.updateItems();
          }
          this.setState({ invalidCampaignCode: undefined, campaignCodeValue: undefined });
        })
        .catch(error => {
          console.error(error);
        });
    }
  }

  expandButtonHandler() {
    this.setState({ isComponentExpanded: true });
  }

  render() {
    const { activeCampaignCode, avardaSessionId } = this.props;
    const { isComponentExpanded, invalidCampaignCode } = this.state;
    
    if (!avardaSessionId) {
      return false;
    }

    // component is expanded with an active code, render the active code
    if (activeCampaignCode) {
      return (
        <CampaignCodeContainer>
          <ActiveCodeTitle>{websiteStrings.campaignCodeActiveTitle}</ActiveCodeTitle>
          <CampaignCodeInputContainer>
            <ActiveCode>
              {activeCampaignCode}
              <RemoveCampaignCode onClick={this.removeCampaignCodeHandler} />
            </ActiveCode>
          </CampaignCodeInputContainer>
          <InputError />
        </CampaignCodeContainer>
      );
    }

    // component is expanded without a active code, render the input field
    if (isComponentExpanded && !activeCampaignCode) {
      return (
        <CampaignCodeContainer>
          <form onSubmit={this.formHandler}>
            <CampaignCodeLabel htmlFor="campaign-code-input">{websiteStrings.campaignCodeInputLabel}</CampaignCodeLabel>
            <CampaignCodeInputContainer>
              <CampaignCodeInputElement
                name="campaign-code"
                id="campaign-code-input"
                onChange={this.inputChangeHandler}
              />
              <CampaignCodeButton type="submit">{websiteStrings.campaignCodeSubmitLabel}</CampaignCodeButton>
            </CampaignCodeInputContainer>
            {invalidCampaignCode ? (
              <InputError>
                {invalidCampaignCode} {websiteStrings.campaignCodeError}
              </InputError>
            ) : (
              <InputError />
            )}
          </form>
        </CampaignCodeContainer>
      );
    }

    // component is not expanded, render expand button
    return (
      <CampaignCodeContainer>
        <ExpandButton onClick={this.expandButtonHandler}>{websiteStrings.campaignCodeInputLabel}</ExpandButton>
      </CampaignCodeContainer>
    );
  }
}

CampaignCodeComponent.defaultProps = {
  activeCampaignCode: undefined,
};

CampaignCodeComponent.propTypes = {
  fetchCart: PropTypes.func.isRequired,
  avardaSessionId: PropTypes.string.isRequired,
  activeCampaignCode: PropTypes.string,
};

export const CampaignCodeInput = connect(
  CampaignCodeComponent,
  { fetchCart },
  store => ({
    activeCampaignCode: store.cart.get('campaignCode')
  })
);

const CampaignCodeContainer = styled.div`
  margin: 40px 0 20px;
`;

const ActiveCodeTitle = styled.p`
  ${fontFamilyBody};
  display: block;
  margin: 0;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  font-weight: 600;
`;

const CampaignCodeLabel = ActiveCodeTitle.withComponent('label');
const ExpandButton = ActiveCodeTitle.extend`
  cursor: pointer;
  background-color: transparent;
  border: none;
  padding: 0;
`.withComponent('button');

const CampaignCodeInputContainer = styled.div`
  width: 100%;
  height: 50px;
  margin: 15px 0 0;
  max-width: 400px;
  display: flex;
`;

const CampaignCodeInputElement = styled.input.attrs({
  type: 'text',
  placeholder: websiteStrings.campaignCodeInputPlaceholder,
  autocomplete: 'off'
})`
  background-color: ${color.white};
  border: solid 1px #dcdcdc;
  color: ${color.black};
  outline: 0;
  font-size: 16px;
  font-weight: normal;
  padding: 15px 40px 15px 14px;
  width: 100%;
  display: inline-block;
  margin: 0;
  -webkit-appearance: none;
  border-radius: 0;

  &::placeholder {
    color: ${color.darkgrey};
    font-style: italic;
  }
`;

const CampaignCodeButton = styled.button`
  ${fontFamilyBody};
  font-size: 13px;
  text-align: center;
  text-transform: uppercase;
  background-color: ${color.primary};
  padding: 0 15px;
  height: 100%;
  border: none;
  font-weight: 600;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: 1.1px;
  color: ${color.white};
  cursor: pointer;
`;

const ActiveCode = styled.div`
  ${fontFamilyBody};
  font-size: 16px;
  text-align: center;
  background-color: ${color.primary};
  padding: 0 55px 0 15px;
  height: 100%;
  border: none;
  font-weight: 600;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: 1.1px;
  color: ${color.white};
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  position: relative;
`;

const RemoveCampaignCode = styled.button.attrs({
  title: websiteStrings.campaignCodeRemoveCodeTitle
})`
  position: absolute;
  top: 0;
  right: 0;
  width: 50px;
  height: 50px;
  border: none;
  cursor: pointer;
  background-color: transparent;
  background-image: url('/images/icons/close-white.svg');
  background-position: center;
  background-repeat: no-repeat;
  background-size: 10px;
`;

const InputError = styled.p`
  margin: 0.5em 0;
  min-height: 17px;
  font-size: 12px;
  color: ${color.warning};
`;



// WEBPACK FOOTER //
// ./src/components/CampaignCodeInput.js